import { useState } from "react";
import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import GAData from "@components/GAData/GAData";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import Header from "@components/Header/Header";
import LoadMore from "@components/LoadMore/LoadMore";
import MetaTags from "@components/MetaTags/MetaTags";
import EventsAwardsLogoMenu from "@events-awards/components/EventsAwardsLogoMenu";
import { AWARDS_EVENTS_PAGE_ROW_DATA } from "@events-awards/utils/constants";
import { processArticleElements } from "@events-awards/utils/helpers";
import {
  AwardDataRow,
  EventsAwardsWinnersContextData,
} from "@events-awards/utils/types";
import { useLoadMoreEventsWinners } from "@hooks/useLoadMoreEventsWinners";
import { cn } from "@util/helpers";

import SIAFooter from "./SIAFooter";

/**
 * Component to render SIA winners page content
 */
const EventsAwardsSIAWinnerPage = ({
  title,
  articles,
  awardType,
}: EventsAwardsWinnersContextData): React.ReactElement => {
  const { articleList, handleLoadMore, hasMoreArticles } =
    useLoadMoreEventsWinners(articles, 10, awardType);
  const [showTabs, setShowTabs] = useState(false);

  const years = articleList.map(({ title }) => title);
  const [selectedYear, setSelectedYear] = useState(years[0]);

  const awardData: AwardDataRow | undefined = AWARDS_EVENTS_PAGE_ROW_DATA.find(
    (data) => data.awardType === awardType
  );
  const awardPath = awardData?.blockTitleProps?.link || "";

  return (
    <div
      className="min-h-screen w-full bg-gray-50"
      data-testid="events-awards-sia-winners-page"
    >
      <AdSettings
        adNames={["lb1", "prestitial", "catfish", "abm"]}
        path={`${awardPath}/winners`}
        adTargets={[{ key: "page", value: "listing" }]}
      />

      <GAData
        title={`${awardData?.blockTitleProps?.text} Winners`}
        level2="events awards"
      />

      <MetaTags
        title={title}
        description={`The Business Times ${awardData?.blockTitleProps?.text} - ${awardData?.description}`}
        ogType="website"
        slug={awardPath}
      />

      <Ad
        adType="lb1"
        className="flex min-h-[50px] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[250px]"
      />

      <Header />

      <Container>
        <Row rootClassName="justify-center">
          <Column rootClassName="w-full lg:w-10/12 text-center my-8">
            <EventsAwardsLogoMenu awardType={awardType} />

            <div className="mx-auto max-w-4xl py-8">
              <div>
                <button
                  onClick={() => {
                    setShowTabs(!showTabs);
                  }}
                  className="mb-2 flex items-center gap-2 text-left text-2xl font-bold focus:outline-none"
                >
                  <BlockTitle
                    text={selectedYear}
                    rootClassName="text-4xl md:text-6xl lg:text-8xl font-medium"
                  />

                  <svg
                    className={`h-4 w-4 transform transition-transform duration-300 ${
                      showTabs ? "rotate-180" : "rotate-0"
                    }`}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 11.584l3.71-4.354a.75.75 0 111.14.976l-4.25 5a.75.75 0 01-1.14 0l-4.25-5a.75.75 0 01.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>

              {showTabs ? (
                <div className="mb-2 flex flex-wrap gap-4 border border-gray-200 p-2 shadow-sm">
                  {years.map((year) => (
                    <button
                      key={year}
                      onClick={() => {
                        setSelectedYear(year);
                        setShowTabs(false);
                      }}
                      className={`px-4 py-2 text-sm font-semibold transition-colors ${
                        selectedYear === year
                          ? "bg-gray-700 text-white"
                          : "bg-white text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              ) : null}

              <div className="border-t border-gray-100 py-6">
                <div className="space-y-8">
                  {articleList.find((article) => article.title === selectedYear)
                    ?.elements &&
                    processArticleElements(
                      articleList.find(
                        (article) => article.title === selectedYear
                      )?.elements || []
                    ).map((award, index) => (
                      <div key={index}>
                        <div>
                          <p
                            className={cn(
                              "tracking-wider text-left text-sm font-semibold",
                              { "first:underline": !award.recipient }
                            )}
                          >
                            {award.title}
                          </p>
                        </div>

                        <div className="space-y-1 text-left">
                          {award.recipient ? (
                            award.type === "paragraph" ? (
                              <p className="text-sm">{award.recipient}</p>
                            ) : (
                              <div data-testid="table-component">
                                <table className="w-full table-auto">
                                  <tbody className="dark:bg-slate-800 bg-gray-50">
                                    {(typeof award.recipient === "string"
                                      ? JSON.parse(award.recipient)
                                      : award.recipient
                                    ).map((row: string[], rowIndex: number) => (
                                      <tr key={rowIndex}>
                                        {row.map((cell, cellIndex) => (
                                          <td
                                            key={cellIndex}
                                            className="border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-400 border-b p-4"
                                          >
                                            {cell}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )
                          ) : null}
                        </div>

                        <div className="space-y-1 text-left">
                          {award.position ? (
                            <p className="mb-0 text-sm">{award.position}</p>
                          ) : null}

                          {award.organization ? (
                            <p className="text-sm">{award.organization}</p>
                          ) : null}
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {hasMoreArticles ? (
                <LoadMore
                  onLoadMore={handleLoadMore}
                  hasMore={hasMoreArticles}
                  loadText="MORE"
                />
              ) : null}
            </div>
          </Column>
        </Row>
      </Container>

      <SIAFooter />

      <Ad adType="catfish" />

      <Ad adType="abm" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default EventsAwardsSIAWinnerPage;
